import { flexPos } from '@/assets/styles/mixin';
import { FC, ReactNode } from 'react';
import { styled } from 'styled-components';
import { EMessageStatus, EContentType, IMessage } from '@/store/messageStore';
import { base64ToFile, clone, getLocalTime } from '@/utils';
import ProgressCircle from './ProgressCircle';
import useMessageStore from '@/store/messageStore';
import Image from '@cps/MediaComp/Image';
import { v4 as uuidV4 } from 'uuid';
import { BubbleReadStatus } from './BubbleStatus';
import { useUpload } from '@cps/Upload/useUpload';
import { EFaqKnows, useChat, useFaq, useMessageHistory } from '@/service/useChat';
import { localMemory } from 'localmemory';
import { validImage } from '@/utils/valid';
import { errorMsg } from '@/service/serverCode';
import { useCreateChat } from '@/service/useChatConfig';
import { isSimpleMediaSize } from '../Upload/tools';
import { getImageDimensions } from '../Upload/mediaCompression';
import useUserStore from '@/store/userStore';

interface IImgBubbleProps {
  msgBody: string;
  status: EMessageStatus;
  isMe?: boolean;
  messageItem: IMessage;
  isRead: boolean;
  progress: number;
  isStopUpload?: boolean;
}

export const ImgBubbleWrap = styled.div<{ $isMe?: boolean }>`
  position: relative;
  ${flexPos('center')}
  justify-content: ${(props) => (props.$isMe ? 'flex-end' : 'flex-start')};
  .bubble-status {
    width: 48rem;
    margin-right: 16rem;
  }
`;

const ImgBubble: FC<IImgBubbleProps> = ({
  msgBody,
  isMe,
  status,
  messageItem,
  isRead,
  progress,
  isStopUpload,
}) => {
  const messageStore = useMessageStore();
  const userStore = useUserStore();
  const { doUpload, progressNum } = useUpload();
  const { msgSend } = useChat();
  const { createSession } = useCreateChat();
  const { seqInfo, getSessionList } = useMessageHistory();
  const { getFaqKnows } = useFaq();

  const imgUrl = useMemo(() => {
    if (!msgBody || (typeof msgBody === 'object' && !Object.keys(msgBody).length)) {
      return '';
    }

    let srcObj = {
      thumbnail: { key: '', url: '' },
      resource: { key: '', url: '' },
    };
    try {
      const temp = JSON.parse(msgBody);
      srcObj = temp;
    } catch (error) {}
    if (srcObj?.resource?.url) {
      return srcObj;
    }

    const content = base64ToFile(msgBody, `${uuidV4()}.png`);
    return URL.createObjectURL(content);
  }, [msgBody]);

  const getMediaInxById = (uniqueId: string) => {
    const mediaMsgList = messageStore.getMediaMsgList();
    return mediaMsgList.findIndex((messageItem) => messageItem.uniqueId === uniqueId);
  };

  const messageContainerRef = useRef<HTMLElement | null>();
  useEffect(() => {
    messageContainerRef.current = document.getElementById('messageContainer');
  }, []);

  return (
    <ImgBubbleWrap $isMe={isMe}>
      <BubbleReadStatus isRead={!!isMe && isRead} />

      <ProgressCircle
        isStopUpload={isStopUpload}
        className="ml-16"
        progress={progress}
        status={status}
        isMe={!!isMe}
        onClickMask={async () => {
          if (status === EMessageStatus.fail && !messageItem.isLockClickUpload) {
            const cloneMsgItem = clone(messageItem, true);
            // cloneMsgItem.isStopUpload = !cloneMsgItem.isStopUpload;
            messageStore.updateIsLockClickUpload(cloneMsgItem, true);

            const restoredFile = new File(
              [messageItem.fileData?.content] as any,
              messageItem.fileData?.metadata.name as any,
              {
                type: messageItem.fileData?.metadata.type,
                lastModified: Date.now(),
              }
            );

            if (messageItem) {
              const ff = base64ToFile(messageItem.msgBody);
              const thumbnail = await doUpload(ff);
              let imageSize = {
                width: 100,
                height: 100,
              };
              imageSize = await getImageDimensions(ff);

              cloneMsgItem.thumbnail = JSON.stringify(thumbnail);
              messageStore.updateMessageThumbnail(cloneMsgItem);

              let src;
              try {
                src = await doUpload(
                  restoredFile,
                  true,
                  (progress) => {
                    const tempMsg = clone(cloneMsgItem, true);
                    tempMsg.status = EMessageStatus.pending;
                    tempMsg.progress = progress;

                    messageStore.updateMessageProgress2(tempMsg);
                  },
                  (uploadId) => {
                    console.log('停止callback...', uploadId);
                    cloneMsgItem.uploadId = uploadId;
                    messageStore.updateMessageUploadIdAndThumbnail(cloneMsgItem);

                    const tempMsgList = messageStore.getMsgList();
                    const targetMsg = tempMsgList.find((item) => {
                      return item.uniqueId === cloneMsgItem.uniqueId;
                    });
                    console.log('targetMsg...', targetMsg);
                    return targetMsg?.isStopUpload || false;
                  }
                );
              } catch (error) {
                console.log('doupload repeat error', error);
              }

              const isImage = messageItem.msgType === EContentType.image;
              // console.log('原来的缩略图...', messageItem.thumbnail);

              // let tempThumbnail = '';
              // try {
              //   tempThumbnail = JSON.parse(messageItem.thumbnail!);
              // } catch (error) {}

              if (src?.url) {
                const resource = isImage
                  ? {
                      key: src.key,
                      url: src.url,
                    }
                  : {
                      key: '',
                      url: src.url,
                    };

                const doSend = async () => {
                  const resp = await msgSend({
                    msgBody: JSON.stringify({
                      thumbnail: thumbnail,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    }),
                    msgSeq: cloneMsgItem.msgSeq,
                    msgType: cloneMsgItem.msgType,
                    clientMsgID: cloneMsgItem.clientMsgID,
                    sendTime: cloneMsgItem.sendTime,
                  });

                  if (resp.success) {
                    const cloneMsgpack = clone(cloneMsgItem, true);
                    cloneMsgpack.msgBody = JSON.stringify({
                      thumbnail: thumbnail,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    });
                    messageStore.setLatestSeq(resp.data?.msgReq || 0);
                    cloneMsgpack.status = EMessageStatus.success;
                    cloneMsgpack.msgSeq = resp.data?.msgReq!;
                    cloneMsgpack.sessionId = userStore.getSessionConfig()?.sessionId;
                    // messageStore.updateMessageItem(msgPack);
                    // await localforage.setItem(sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg', msgList);

                    messageStore.updateMessageItem(cloneMsgpack);
                  } else if (
                    [
                      errorMsg.sessionError.code,
                      errorMsg.sessionFail.code,
                      errorMsg.sessionTimeout.code,
                    ].includes(resp.code as any)
                  ) {
                    const resp = await createSession();

                    const sessionList = await getSessionList();
                    seqInfo.current.sessionList = sessionList;

                    if (resp.code === 0) {
                      cloneMsgItem.sessionId = resp.data.sessionId;
                      const cloneMsgpack = clone(cloneMsgItem, true);
                      messageStore.updateMsgSessionId(cloneMsgpack);
                      await doSend();
                      await getFaqKnows(EFaqKnows.knows, '');
                    }
                  }
                };

                doSend();
              }
            }

            messageStore.updateIsLockClickUpload(cloneMsgItem, false);
          }

          const _file = messageItem.file || messageItem?.fileData?.content;

          const isSimple = isSimpleMediaSize(_file?.size || 0);

          if (!isSimple && status === EMessageStatus.pending) {
            const cloneMsgItem = clone(messageItem, true);

            try {
              cloneMsgItem.isStopUpload = !cloneMsgItem.isStopUpload;
              messageStore.updateMessageItem(cloneMsgItem);
            } catch (error) {
            }

            let restoredFile;
            if (messageItem.fileData) {
              restoredFile = new File(
                [messageItem.fileData?.content] as any,
                messageItem.fileData?.metadata.name as any,
                {
                  type: messageItem.fileData?.metadata.type,
                  lastModified: Date.now(),
                }
              );
            } else {
              restoredFile = _file;
            }

            if (restoredFile && messageItem.isStopUpload) {
              const imageSize = await getImageDimensions(restoredFile);

              let src;
              try {
                src = await doUpload(
                  restoredFile,
                  true,
                  (progress) => {
                    const tempMsg = clone(cloneMsgItem, true);
                    tempMsg.progress = progress;
                    messageStore.updateMessageProgress(tempMsg);
                  },
                  () => {
                    const tempMsgList = messageStore.getMsgList();
                    const targetMsg = tempMsgList.find((item) => {
                      return item.uniqueId === cloneMsgItem.uniqueId;
                    });
                    console.log('targetMsg...', targetMsg);
                    return targetMsg?.isStopUpload || false;
                  },
                  'repeat',
                  messageItem?.uploadId
                );
              } catch (error) {
                console.log('doupload repeat error', error);
              }

              const isImage = validImage(restoredFile.type);

              let tempThumbnail = '';
              try {
                tempThumbnail = JSON.parse(messageItem.thumbnail!);
              } catch (error) {}

              if (src?.url) {
                const resource = isImage
                  ? {
                      key: src.key,
                      url: src.url,
                    }
                  : {
                      key: '',
                      url: src.url,
                    };

                const doSend = async () => {
                  const resp = await msgSend({
                    msgBody: JSON.stringify({
                      thumbnail: resource,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    }),
                    msgSeq: cloneMsgItem.msgSeq,
                    msgType: cloneMsgItem.msgType,
                    clientMsgID: cloneMsgItem.clientMsgID,
                    sendTime: cloneMsgItem.sendTime,
                  });

                  if (resp.success) {
                    const cloneMsgpack = clone(cloneMsgItem, true);
                    cloneMsgpack.msgBody = JSON.stringify({
                      thumbnail: resource,
                      resource,
                      width: imageSize.width,
                      height: imageSize.height,
                    });
                    messageStore.setLatestSeq(resp.data?.msgReq || 0);
                    cloneMsgpack.status = EMessageStatus.success;
                    cloneMsgpack.msgSeq = resp.data?.msgReq!;
                    // messageStore.updateMessageItem(msgPack);
                    // await localforage.setItem(sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg', msgList);

                    messageStore.updateMessageItem(cloneMsgpack);
                  } else if (
                    [
                      errorMsg.sessionError.code,
                      errorMsg.sessionFail.code,
                      errorMsg.sessionTimeout.code,
                    ].includes(resp.code as any)
                  ) {
                    const resp = await createSession();

                    const sessionList = await getSessionList();
                    seqInfo.current.sessionList = sessionList;

                    if (resp.code === 0) {
                      await doSend();

                      await getFaqKnows(EFaqKnows.knows, '');
                    }
                  }
                };

                doSend();
              }
            }
          }
        }}
      >
        <Image
          onLoad={() => {
            if (messageItem.isInitMsg) {
              setTimeout(() => {
                const target = messageContainerRef.current;
                target &&
                  target.scrollTo({
                    top: target.scrollHeight,
                    behavior: 'smooth',
                  });
              }, 199);
            }
          }}
          onClick={() => {
            const inx = getMediaInxById(messageItem.uniqueId);
            messageStore.setActivePreviewMediaInx(inx);
            messageStore.setShowPreviewMedia(true);
            if (window.parent) {
              window.parent.postMessage({
                type: 'preview',
                visible: true
              }, '*')
            }
          }}
          src={imgUrl}
          className="max-w-320 min-w-180 rounded-32"
        />
      </ProgressCircle>
    </ImgBubbleWrap>
  );
};

export default ImgBubble;
